import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { AdvanceMileDefinitionRequestViewModel } from '../models/AdvanceMileDefinitionRequestViewModel';
import type { AdvanceMileParameterListResponseViewModel } from '../models/AdvanceMileParameterListResponseViewModel';
import type { ClaimMileRequestViewModel } from '../models/ClaimMileRequestViewModel';
import type { ClaimMileResponseViewModel } from '../models/ClaimMileResponseViewModel';
import type { FlightDelayInfoRequestViewModel } from '../models/FlightDelayInfoRequestViewModel';
import type { FlightDelayInfoResponseViewModel } from '../models/FlightDelayInfoResponseViewModel';
import type { FlightDelayRequestViewModel } from '../models/FlightDelayRequestViewModel';
import type { FlightDelayResponseViewModel } from '../models/FlightDelayResponseViewModel';
import type { FlightInfoRequestViewModel } from '../models/FlightInfoRequestViewModel';
import type { FlightInfoResponseViewModel } from '../models/FlightInfoResponseViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class MileService {
  /**
   * Flights
   * Gets flights
   * @returns FlightDelayResponseViewModel Success
   * @throws ApiError
   */
  public static async getFlights(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: FlightDelayRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<FlightDelayResponseViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<FlightDelayResponseViewModel>>({
      method: 'GET',
      url: `/mile/flights`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * ClaimRetardMile
   * Claims reward miles for delay
   * @returns ClaimMileResponseViewModel Success
   * @throws ApiError
   */
  public static async claimRetardMile(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: Array<ClaimMileRequestViewModel>;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<ClaimMileResponseViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<ClaimMileResponseViewModel>>({
      method: 'POST',
      url: `/mile/claimretardmile`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * AdvanceMileParameterList
   * Advance miles Parameter List
   * @returns AdvanceMileParameterListResponseViewModel Success
   * @throws ApiError
   */
  public static async advanceMileParameterList(
    {
      maskedCardNum,
      authorization,
      mileAmount,
      callCenterPaymentRequestId,
    }: {
      maskedCardNum: string;
      /**
       * Authorization
       */
      authorization: string;
      mileAmount?: number;
      callCenterPaymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<AdvanceMileParameterListResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<AdvanceMileParameterListResponseViewModel>({
      method: 'GET',
      url: `/mile/advancemileparameterlist`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(mileAmount ? { mileAmount: mileAmount } : {}),
        ...(maskedCardNum ? { maskedCardNum: maskedCardNum } : {}),
        ...(callCenterPaymentRequestId
          ? { callCenterPaymentRequestId: callCenterPaymentRequestId }
          : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * AdvanceMileDefinition
   * Advance miles
   * @returns any Success
   * @throws ApiError
   */
  public static async advanceMileDefinition(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: AdvanceMileDefinitionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<any> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<any>({
      method: 'POST',
      url: `/mile/advancemiledefinition`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * CallCenter-AdvanceMileParameterList
   * Advance miles Parameter List for CallCenter
   * @returns AdvanceMileParameterListResponseViewModel Success
   * @throws ApiError
   */
  public static async callCenterAdvanceMileParameterList(
    {
      maskedCardNum,
      mileAmount,
      callCenterPaymentRequestId,
    }: {
      maskedCardNum: string;
      mileAmount?: number;
      callCenterPaymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<AdvanceMileParameterListResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<AdvanceMileParameterListResponseViewModel>({
      method: 'GET',
      url: `/mile/callcenter-advancemileparameterlist`,
      query: {
        ...(mileAmount ? { mileAmount: mileAmount } : {}),
        ...(maskedCardNum ? { maskedCardNum: maskedCardNum } : {}),
        ...(callCenterPaymentRequestId
          ? { callCenterPaymentRequestId: callCenterPaymentRequestId }
          : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * CallCenter-AdvanceMileDefinition
   * Advance miles for CallCenter
   * @returns any Success
   * @throws ApiError
   */
  public static async callCenterAdvanceMileDefinition(
    {
      body,
    }: {
      body?: AdvanceMileDefinitionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<any> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<any>({
      method: 'POST',
      url: `/mile/callcenter-advancemiledefinition`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * FlightInfo
   * Gets flight  Info
   * @returns FlightInfoResponseViewModel Success
   * @throws ApiError
   */
  public static async getFlightInfo(
    {
      opaqueId,
      tokenId,
      body,
    }: {
      /**
       * OpaqueId
       */
      opaqueId: string;
      /**
       * TokenId
       */
      tokenId: string;
      body: FlightInfoRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightInfoResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightInfoResponseViewModel>({
      method: 'GET',
      url: `/retard/flightinfo`,
      headers: {
        ...(opaqueId ? { opaqueId: opaqueId } : {}),
        ...(tokenId ? { tokenId: tokenId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * FlightDelayInfo
   * Gets flight delay Info
   * @returns FlightDelayInfoResponseViewModel Success
   * @throws ApiError
   */
  public static async getFlightDelayInfo(
    {
      header,
      body,
    }: {
      /**
       * Authorization
       */
      header: any;
      body?: FlightDelayInfoRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<FlightDelayInfoResponseViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<FlightDelayInfoResponseViewModel>>({
      method: 'GET',
      url: `/retard/flightdelayinfo`,
      headers: {
        ...(header ? { header: header } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
